import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { isHttpClientError } from "../../../lib/http/errors";
import {
  geoGovernanceCandidatesQueryKey,
  geoGovernanceRunQueryKey,
  geoGovernanceRunsQueryKey,
  geoGovernanceRuntimeStatusQueryKey,
  geoGovernanceTargetsQueryKey,
} from "../config";
import {
  approveGeoTarget,
  createGeoRun,
  executeGeoRun,
  fetchGeoAudits,
  fetchGeoCandidates,
  fetchGeoRunDetail,
  fetchGeoRuns,
  fetchGeoRuntimeStatus,
  fetchGeoTargets,
  refreshGeoTargetBusinessData,
  retryGeoTarget,
  skipGeoTarget,
} from "../repo/geoGovernanceRepo";
import type {
  GeoRunCreateInput,
  GeoRunFilters,
  GeoTargetFilters,
  GeoTargetStatus,
  GeoTargetType,
} from "../types";

const defaultRunFilters: GeoRunFilters = { page: 1, pageSize: 20, keyword: undefined, status: undefined };
const defaultTargetFilters: GeoTargetFilters = { page: 1, pageSize: 20, status: "review_required" };

/** 管理历史 GEO 治理页面的查询、执行和审核动作。 */
export function useGeoGovernanceRuntime() {
  const queryClient = useQueryClient();
  const [runFilters, setRunFilters] = useState(defaultRunFilters);
  const [targetFilters, setTargetFilters] = useState(defaultTargetFilters);
  const [selectedRunId, setSelectedRunId] = useState<string>();
  const [selectedTargetId, setSelectedTargetId] = useState<string>();
  const [actionErrorMessage, setActionErrorMessage] = useState("");
  const [remoteExecution, setRemoteExecution] = useState<{
    runId: string;
    updatedAt?: string;
  }>();

  const runtimeStatusQuery = useQuery({
    queryKey: geoGovernanceRuntimeStatusQueryKey,
    queryFn: fetchGeoRuntimeStatus,
    refetchOnWindowFocus: false,
  });

  const runsQuery = useQuery({
    queryKey: [...geoGovernanceRunsQueryKey, runFilters],
    queryFn: () => fetchGeoRuns(runFilters),
    refetchOnWindowFocus: false,
  });
  const runDetailQuery = useQuery({
    queryKey: selectedRunId ? geoGovernanceRunQueryKey(selectedRunId) : ["geo-governance", "run", "none"],
    queryFn: () => fetchGeoRunDetail(selectedRunId as string),
    enabled: Boolean(selectedRunId),
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (query.state.data?.status === "running" ? 5000 : false),
  });
  const targetsQuery = useQuery({
    queryKey: selectedRunId
      ? geoGovernanceTargetsQueryKey(selectedRunId, targetFilters)
      : ["geo-governance", "targets", "none"],
    queryFn: () => fetchGeoTargets(selectedRunId as string, targetFilters),
    enabled: Boolean(selectedRunId),
    refetchOnWindowFocus: false,
  });
  const candidatesQuery = useQuery({
    queryKey: selectedTargetId
      ? geoGovernanceCandidatesQueryKey(selectedTargetId)
      : ["geo-governance", "candidates", "none"],
    queryFn: () => fetchGeoCandidates(selectedTargetId as string),
    enabled: Boolean(selectedTargetId),
  });
  const auditsQuery = useQuery({
    queryKey: selectedTargetId
      ? ["geo-governance", "audits", selectedTargetId]
      : ["geo-governance", "audits", "none"],
    queryFn: () => fetchGeoAudits(selectedTargetId as string),
    enabled: Boolean(selectedTargetId),
  });

  const invalidateRun = async (runId?: string) => {
    await queryClient.invalidateQueries({ queryKey: geoGovernanceRunsQueryKey });
    if (runId) {
      await queryClient.invalidateQueries({ queryKey: geoGovernanceRunQueryKey(runId) });
      await queryClient.invalidateQueries({ queryKey: ["geo-governance", "targets", runId] });
    }
  };

  const createMutation = useMutation({
    mutationFn: createGeoRun,
    onSuccess: async (run) => {
      setSelectedRunId(run.id);
      setTargetFilters({ ...defaultTargetFilters });
      await invalidateRun(run.id);
    },
  });
  const executeMutation = useMutation({
    mutationFn: ({ runId, limit }: { runId: string; limit: number }) => executeGeoRun(runId, limit),
    onSettled: async (result, _error, variables) => invalidateRun(result?.run.id ?? variables.runId),
  });
  const approveMutation = useMutation({
    mutationFn: ({ targetId, candidateId, note }: { targetId: string; candidateId: string; note: string }) =>
      approveGeoTarget(targetId, candidateId, note),
    onSuccess: async () => invalidateRun(selectedRunId),
  });
  const skipMutation = useMutation({
    mutationFn: ({ targetId, note }: { targetId: string; note: string }) => skipGeoTarget(targetId, note),
    onSuccess: async () => invalidateRun(selectedRunId),
  });
  const retryMutation = useMutation({
    mutationFn: (targetId: string) => retryGeoTarget(targetId),
    onSuccess: async () => invalidateRun(selectedRunId),
  });
  const refreshBusinessDataMutation = useMutation({
    mutationFn: (targetId: string) => refreshGeoTargetBusinessData(targetId),
  });

  useEffect(() => {
    if (!remoteExecution || remoteExecution.runId !== selectedRunId) return;
    const updatedAt = runDetailQuery.data?.updatedAt;
    if (updatedAt && updatedAt !== remoteExecution.updatedAt) {
      setRemoteExecution(undefined);
    }
  }, [remoteExecution, runDetailQuery.data?.updatedAt, selectedRunId]);

  /**
   * 统一执行页面动作并转换为可展示的错误信息。
   * @param action 需要执行的异步动作
   * @param message 动作失败时的中文前缀
   * @returns 异步动作结果，失败时返回 undefined
   */
  async function runAction<T>(action: () => Promise<T>, message: string) {
    setActionErrorMessage("");
    try {
      return await action();
    } catch (error) {
      setActionErrorMessage(error instanceof Error ? `${message}：${error.message}` : message);
      return undefined;
    }
  }

  return {
    runtimeStatus: runtimeStatusQuery.data,
    loadingRuntimeStatus: runtimeStatusQuery.isLoading,
    runs: runsQuery.data?.items ?? [],
    runsTotal: runsQuery.data?.total ?? 0,
    runFilters,
    selectedRunId,
    selectedRun: runDetailQuery.data,
    targetFilters,
    targets: targetsQuery.data?.items ?? [],
    targetsTotal: targetsQuery.data?.total ?? 0,
    selectedTargetId,
    candidates: candidatesQuery.data ?? [],
    audits: auditsQuery.data ?? [],
    loadingRuns: runsQuery.isLoading,
    loadingTargets: targetsQuery.isLoading,
    loadingCandidates: candidatesQuery.isLoading,
    errorMessage:
      actionErrorMessage ||
      (remoteExecution?.runId === selectedRunId
        ? "当前分块仍在后台执行，完成后页面会自动刷新并恢复继续执行。"
        : "") ||
      (runtimeStatusQuery.error ? "GEO 治理运行环境状态加载失败，已禁止执行批次。" : "") ||
      (runsQuery.error ? "GEO 治理批次加载失败，请稍后重试。" : "") ||
      (targetsQuery.error ? "GEO 治理对象加载失败，请稍后重试。" : ""),
    creating: createMutation.isPending,
    executing: executeMutation.isPending || remoteExecution?.runId === selectedRunId,
    reviewingTargetId:
      approveMutation.isPending
        ? approveMutation.variables?.targetId
        : skipMutation.isPending
          ? skipMutation.variables?.targetId
          : retryMutation.isPending
            ? retryMutation.variables
            : refreshBusinessDataMutation.isPending
              ? refreshBusinessDataMutation.variables
              : undefined,
    setSelectedRunId,
    setSelectedTargetId,
    setRunPage(page: number) {
      setRunFilters((current) => ({ ...current, page }));
    },
    setRunPageSize(pageSize: number) {
      setRunFilters((current) => ({ ...current, page: 1, pageSize }));
    },
    submitRunFilters(keyword: string, status?: GeoRunFilters["status"]) {
      setRunFilters((current) => ({ ...current, page: 1, keyword: keyword.trim() || undefined, status }));
    },
    setTargetPage(page: number) {
      setTargetFilters((current) => ({ ...current, page }));
    },
    setTargetPageSize(pageSize: number) {
      setTargetFilters((current) => ({ ...current, page: 1, pageSize }));
    },
    setTargetStatus(status?: GeoTargetStatus) {
      setTargetFilters((current) => ({ ...current, page: 1, status }));
    },
    setTargetType(targetType?: GeoTargetType) {
      setTargetFilters((current) => ({ ...current, page: 1, targetType }));
    },
    async createRun(input: GeoRunCreateInput) {
      return runAction(() => createMutation.mutateAsync(input), "创建 GEO 治理批次失败");
    },
    async executeRun(runId: string, limit: number) {
      setActionErrorMessage("");
      setRemoteExecution(undefined);
      try {
        return await executeMutation.mutateAsync({ runId, limit });
      } catch (error) {
        if (isHttpClientError(error) && error.statusCode === 409) {
          setRemoteExecution({ runId, updatedAt: runDetailQuery.data?.updatedAt });
          return undefined;
        }
        setActionErrorMessage(
          error instanceof Error ? `执行 GEO 治理批次失败：${error.message}` : "执行 GEO 治理批次失败",
        );
        return undefined;
      }
    },
    async approveTarget(targetId: string, candidateId: string, note: string) {
      return runAction(() => approveMutation.mutateAsync({ targetId, candidateId, note }), "审核写回 GEO 失败");
    },
    async skipTarget(targetId: string, note: string) {
      return runAction(() => skipMutation.mutateAsync({ targetId, note }), "跳过 GEO 对象失败");
    },
    async retryTarget(targetId: string) {
      return runAction(() => retryMutation.mutateAsync(targetId), "重试 GEO 对象失败");
    },
    async refreshTargetAfterBusinessFix(targetId: string, runId: string) {
      return runAction(async () => {
        const refreshed = await refreshBusinessDataMutation.mutateAsync(targetId);
        if (refreshed.status === "pending") {
          await executeMutation.mutateAsync({ runId, limit: 1 });
        }
        setTargetFilters((current) => ({ ...current, page: 1 }));
        await invalidateRun(runId);
        return refreshed;
      }, "同步业务资料并重新核验失败");
    },
  };
}
